import { Injectable } from '@angular/core';
import { Appointment } from '../models/appointment.model';

/**
 * CalendarService – generates .ics files and Google Calendar links.
 * No external API needed for this mock – everything runs client-side.
 *
 * TODO (real backend): after appointment is confirmed, call Google Calendar API
 * with an OAuth2 token to insert the event server-side and store eventId.
 */
@Injectable({ providedIn: 'root' })
export class CalendarService {

  /**
   * Generate and download a standard .ics file.
   * Works with Google Calendar, Apple Calendar, Outlook.
   */
  downloadIcs(appointment: Appointment): void {
    const content = this.buildIcs(appointment);
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `turno-${appointment.date}-${appointment.time.replace(':', '')}.ics`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  /**
   * Open Google Calendar "add event" page in a new tab.
   * No OAuth needed – a pre-filled form opens for the user to click Save.
   */
  openGoogleCalendarLink(appointment: Appointment): void {
    const url = this.buildGoogleCalendarUrl(appointment);
    window.open(url, '_blank', 'noopener');
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  private buildIcs(a: Appointment): string {
    const start = this.toIcsDate(a.date, a.time);
    const end   = this.toIcsDate(a.date, this.addMinutes(a.time, a.duration));
    const now   = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const uid   = `${a.id}@turnero2026`;

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Turnero2026//Fluid Architect//ES',
      'CALSCALE:GREGORIAN',
      'METHOD:REQUEST',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${a.serviceType} con ${a.professionalName}`,
      `DESCRIPTION:${a.notes ?? 'Turno confirmado en Fluid Architect Scheduler.'}`,
      `ORGANIZER;CN=${a.professionalName}:MAILTO:${a.clientEmail}`,
      `ATTENDEE;CN=${a.clientName};ROLE=REQ-PARTICIPANT:MAILTO:${a.clientEmail}`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
  }

  private buildGoogleCalendarUrl(a: Appointment): string {
    const start = this.toGoogleDate(a.date, a.time);
    const end   = this.toGoogleDate(a.date, this.addMinutes(a.time, a.duration));
    const title = encodeURIComponent(`${a.serviceType} con ${a.professionalName}`);
    const details = encodeURIComponent(
      a.notes ?? 'Turno reservado vía Fluid Architect Scheduler.'
    );
    return (
      `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${title}` +
      `&dates=${start}/${end}` +
      `&details=${details}`
    );
  }

  /** 'YYYY-MM-DD' + 'HH:mm' → '20241003T100000' */
  private toIcsDate(date: string, time: string): string {
    return date.replace(/-/g, '') + 'T' + time.replace(':', '') + '00';
  }

  /** 'YYYY-MM-DD' + 'HH:mm' → '20241003T100000Z' (UTC format for Google) */
  private toGoogleDate(date: string, time: string): string {
    return date.replace(/-/g, '') + 'T' + time.replace(':', '') + '00Z';
  }

  /** Add minutes to a time string 'HH:mm' */
  private addMinutes(time: string, minutes: number): string {
    const [h, m] = time.split(':').map(Number);
    const total = h * 60 + m + minutes;
    const hh = Math.floor(total / 60).toString().padStart(2, '0');
    const mm = (total % 60).toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }
}
